
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useUser } from '@/contexts/UserContext';
import { Rectificativa, Cliente } from "@shared/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { EntornoAEATBadge } from '@/components/verifactu/EntornoAEATBadge';
import { Plus, Pencil, Loader2, FileText, FileCode, Send, ShieldCheck, ShieldAlert, Ban } from 'lucide-react';

const getStatusBadgeVariant = (status: Rectificativa['estado']) => {
  return status === 'Borrador' ? 'secondary' : 'default';
};

export default function RectificativasPage() {
  const { fetchWithAuth } = useUser();
  const [rectificativas, setRectificativas] = useState<Rectificativa[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [anularId, setAnularId] = useState<string | null>(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState<string>('');
  const [selectedRectificativas, setSelectedRectificativas] = useState<Set<string>>(new Set());
  const [isSendingToAEAT, setIsSendingToAEAT] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [rectRes, cliRes] = await Promise.all([
        fetchWithAuth("/api/rectificativas"),
        fetchWithAuth("/api/clientes"),
      ]);
      if (!rectRes.ok || !cliRes.ok) throw new Error("No se pudieron obtener los datos.");
      const [rectData, cliData] = await Promise.all([rectRes.json(), cliRes.json()]);
      setRectificativas(rectData);
      setClientes(cliData);
    } catch (error) {
      toast.error("Error al cargar datos", { description: error instanceof Error ? error.message : "Error desconocido" });
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const enrichedRectificativas = useMemo(() => {
    const clienteMap = new Map(clientes.map(c => [c.id, c.nombre]));
    return rectificativas.map(r => ({
      ...r,
      clienteNombre: clienteMap.get(r.clienteId) || 'N/A',
    }));
  }, [rectificativas, clientes]);

  const handleDownloadPDF = (rectificativaId: string) => {
    navigate(`/rectificativas/${rectificativaId}/edit?showPreview=true`);
  };

  const handleDownloadXML = async (rectificativa: Rectificativa) => {
    if (rectificativa.signedXml) {
      const blob = new Blob([rectificativa.signedXml], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rectificativa-${rectificativa.numero}-firmada.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('XML Facturae descargado', {
        description: `Firmado el ${new Date(rectificativa.signedAt!).toLocaleDateString('es-ES')}`
      });
    } else {
      toast.info('Factura rectificativa sin firmar', {
        description: 'Esta factura rectificativa aún no ha sido firmada con certificado digital. Edita la factura y pulsa "Registrar con Certificado".'
      });
    }
  };

  const handleAnularConfirm = () => {
    if (!anularId) return;

    // Validar que se haya ingresado un motivo
    if (!motivoAnulacion.trim()) {
      toast.error('Motivo requerido', {
        description: 'Debes especificar el motivo de la anulación según normativa VeriFactu'
      });
      return;
    }

    const promise = fetchWithAuth(`/api/rectificativas/${anularId}/anular`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motivoAnulacion: motivoAnulacion.trim() })
    }).then(res => {
      if (!res.ok) throw new Error('No se pudo anular la factura rectificativa');
      return res.json();
    });

    toast.promise(promise, {
      loading: 'Anulando factura rectificativa...',
      success: () => {
        fetchData();
        setAnularId(null);
        setMotivoAnulacion('');
        return 'Factura rectificativa anulada con éxito';
      },
      error: (err) => {
        setMotivoAnulacion('');
        return `Error: ${err.message}`;
      },
    });
  };

  const handleCancelAnular = () => {
    setAnularId(null);
    setMotivoAnulacion('');
  };

  const toggleRectificativaSelection = (rectificativaId: string | undefined) => {
    if (!rectificativaId) return;
    setSelectedRectificativas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rectificativaId)) {
        newSet.delete(rectificativaId);
      } else {
        newSet.add(rectificativaId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedRectificativas.size === enrichedRectificativas.length) {
      setSelectedRectificativas(new Set());
    } else {
      const allIds = enrichedRectificativas
        .filter(r => r.verifactu && r.estado !== 'Borrador' && !r.enviadaAEAT)
        .map(r => r.id!)
        .filter(Boolean);
      setSelectedRectificativas(new Set(allIds));
    }
  };

  const handleSendToAEAT = async () => {
    if (selectedRectificativas.size === 0) {
      toast.error('No hay rectificativas seleccionadas', {
        description: 'Selecciona al menos una factura rectificativa para enviar'
      });
      return;
    }

    const rectificativasArray = Array.from(selectedRectificativas);

    // Verificar que todas las rectificativas tengan Verifactu
    const rectificativasNoRegistradas = rectificativasArray.filter(id => {
      const rectificativa = rectificativas.find(r => r.id === id);
      return !rectificativa?.verifactu || rectificativa.estado === 'Borrador';
    });

    if (rectificativasNoRegistradas.length > 0) {
      toast.error('Rectificativas no válidas seleccionadas', {
        description: `${rectificativasNoRegistradas.length} rectificativa(s) sin registro Verifactu o en borrador`
      });
      return;
    }

    setIsSendingToAEAT(true);

    try {
      const res = await fetchWithAuth('/api/rectificativas/enviar-aeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Sin `entorno`: lo decide el servidor con la config VeriFactu del usuario.
        body: JSON.stringify({
          rectificativaIds: rectificativasArray
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Error al enviar a AEAT');
      }

      const data = await res.json();

      if (data.success) {
        toast.success('Envío a AEAT completado', {
          description: `${data.estadisticas.exitosas} rectificativas enviadas correctamente`
        });
        setSelectedRectificativas(new Set());
        fetchData(); // Recargar para actualizar estados
      } else {
        toast.error('Error en el envío', {
          description: data.message || 'Algunas rectificativas no pudieron enviarse'
        });
      }
    } catch (error) {
      toast.error('Error al enviar a AEAT', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
    } finally {
      setIsSendingToAEAT(false);
    }
  };

  const formatCurrency = (value: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tighter">Facturas Rectificativas</h1>
          <p className="text-muted-foreground mt-1">Gestiona tus abonos y facturas rectificativas.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <EntornoAEATBadge />
          {selectedRectificativas.size > 0 && (
            <Button
              onClick={handleSendToAEAT}
              disabled={isSendingToAEAT}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSendingToAEAT ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Enviar a AEAT ({selectedRectificativas.size})
                </>
              )}
            </Button>
          )}
          <Button onClick={() => navigate('/rectificativas/nueva')}><Plus className="mr-2 h-4 w-4" /> Nueva Rectificativa</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Mis Facturas Rectificativas</CardTitle>
          <CardDescription>Lista de todas las facturas rectificativas emitidas.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? <Loader2 className="h-12 w-12 mx-auto my-24 animate-spin text-primary" /> : rectificativas.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <p className="text-muted-foreground">No has creado ninguna factura rectificativa.</p>
              <Button className="mt-4" onClick={() => navigate('/rectificativas/nueva')}>Crear la primera</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedRectificativas.size > 0 && selectedRectificativas.size === enrichedRectificativas.filter(r => r.verifactu && r.estado !== 'Borrador' && !r.enviadaAEAT).length}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Seleccionar todas"
                    />
                  </TableHead>
                  <TableHead>Número</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Factura Original</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Verifactu</TableHead>
                  <TableHead className="text-right pr-6">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrichedRectificativas.map((rect) => (
                  <TableRow key={rect.id} onDoubleClick={() => navigate(`/rectificativas/${rect.id}/edit`)} className="hover:bg-muted/50 cursor-pointer">
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedRectificativas.has(rect.id!)}
                        onCheckedChange={() => toggleRectificativaSelection(rect.id)}
                        disabled={!rect.verifactu || rect.estado === 'Borrador' || rect.enviadaAEAT}
                        aria-label={`Seleccionar rectificativa ${rect.numero}`}
                        title={rect.enviadaAEAT ? `Ya enviada a AEAT el ${new Date(rect.fechaEnvioAEAT!).toLocaleDateString('es-ES')}` : ''}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{rect.numero}</TableCell>
                    <TableCell className="text-muted-foreground">{rect.clienteNombre}</TableCell>
                    <TableCell className="text-muted-foreground">{new Date(rect.fecha).toLocaleDateString()}</TableCell>
                    <TableCell className="text-muted-foreground">{rect.facturaOriginalNumero}</TableCell>
                    <TableCell><Badge variant={getStatusBadgeVariant(rect.estado)}>{rect.estado}</Badge></TableCell>
                    <TableCell className="text-right font-mono font-bold">{formatCurrency(rect.total || 0)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {rect.verifactu && (
                          <>
                            <span aria-label="Registrada en Verifactu"><ShieldCheck className="h-4 w-4 text-green-600" /></span>
                            <span className="text-xs text-muted-foreground" title={`Hash: ${rect.verifactu.hash.substring(0, 8)}...`}>
                              #{rect.verifactu.numeroRegistro}
                            </span>
                          </>
                        )}
                        {!rect.verifactu && rect.estado === 'Borrador' && (
                          <span aria-label="Borrador sin registro"><ShieldAlert className="h-4 w-4 text-muted-foreground" /></span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDownloadPDF(rect.id!)}} title="Descargar PDF"><FileText className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDownloadXML(rect)}} title="Descargar XML Facturae"><FileCode className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/rectificativas/${rect.id}/edit`)}} title="Editar"><Pencil className="h-4 w-4" /></Button>
                          {rect.estado !== 'Borrador' && rect.estado !== 'Anulada' && (
                            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setAnularId(rect.id ?? null)}} title="Anular Factura Rectificativa"><Ban className="h-4 w-4 text-orange-600" /></Button>
                          )}
                        </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!anularId} onOpenChange={handleCancelAnular}>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>Anular Factura Rectificativa</AlertDialogTitle>
                <AlertDialogDescription>
                  La factura rectificativa será marcada como anulada y se mantendrá en el sistema para cumplir con la normativa VeriFactu.
                  {' '}Debes especificar el motivo de la anulación para que se envíe a la AEAT.
                </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <label htmlFor="motivoAnulacionRect" className="text-sm font-medium mb-2 block">
                Motivo de la anulación *
              </label>
              <Input
                id="motivoAnulacionRect"
                placeholder="Ej: Factura rectificativa emitida por error, duplicada, etc."
                value={motivoAnulacion}
                onChange={(e) => setMotivoAnulacion(e.target.value)}
                className="w-full"
                autoFocus
              />
            </div>
            <AlertDialogFooter>
                <AlertDialogCancel onClick={handleCancelAnular}>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleAnularConfirm} className="bg-orange-600 hover:bg-orange-700">
                  Anular Factura Rectificativa
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
